import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, TrendingUp, AlertTriangle, Megaphone, Wallet, Users, Settings2,
  UserCog, CheckCircle2, CalendarDays, Loader2, ShieldCheck, Zap, Clock,
  PauseCircle, Gauge, Target, Feather, Quote, Sparkles, Radio, Calculator, Compass,
  Download, Share2, Check,
} from "lucide-react";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { GFFrame, GFHeader } from "@/components/GFFrame";
import { FactorBar } from "@/components/ScoreVisuals";
import { getAssessment, getPaymentStatus, startPayment, downloadAssessmentPdf, downloadAssessmentSnapshot, aiGetCredits } from "@/lib/gfApi";
import { AiCtaSection, storeAiEmail, EmailCaptureModal } from "@/pages/ai/aiShared";
import { ARCHETYPE_IMAGES } from "@/data/gfContent";

const FACTOR_ORDER = ["hala_tuju", "jenama", "pasaran", "hasil", "pelaksanaan"];

const TONE_STYLE = {
  strength: "bg-emerald-100 text-emerald-700",
  dev: "bg-sky-100 text-sky-700",
  constraint: "bg-amber-100 text-amber-700",
  critical: "bg-rose-100 text-rose-700",
};

const Section = ({ title, kicker, icon: Icon, children, testId }) => (
  <section className="mt-8 px-6" data-testid={testId}>
    <div className="mb-4">
      {kicker && (
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{kicker}</p>
      )}
      <div className="mt-1 flex items-center gap-2">
        {Icon ? <Icon className="h-5 w-5 text-[#0F172A]" /> : <span className="h-5 w-1 rounded-full bg-[#D4AF37]" />}
        <h3 className="font-heading text-xl font-bold text-[#0F172A]">{title}</h3>
      </div>
    </div>
    {children}
  </section>
);

const ActionList = ({ items, icon: Icon = CheckCircle2 }) => (
  <ul className="mt-3 space-y-2">
    {items.map((t, i) => (
      <li key={i} className="flex gap-2.5 text-sm text-slate-600">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" />
        {t}
      </li>
    ))}
  </ul>
);

const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-[#E7E2D6] bg-white p-5 ${className}`}>{children}</div>
);

const inputCls =
  "w-full rounded-2xl border border-[#E7E2D6] bg-white px-4 py-3.5 text-[15px] outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20";

export default function Report() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [checkout, setCheckout] = useState({ email: "", phone: "" });
  const [aiCredits, setAiCredits] = useState(null);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [showEmailCapture, setShowEmailCapture] = useState(false);

  const loadOwner = (email) => {
    if (!email) return;
    setOwnerEmail(email);
    storeAiEmail(email);
    aiGetCredits(email).then(setAiCredits).catch(() => {});
  };

  useEffect(() => {
    (async () => {
      try {
        await getPaymentStatus(id).catch(() => {});
        const data = await getAssessment(id);
        setD(data);
        if (data.paid) {
          if (data.owner_email) loadOwner(data.owner_email);
          else setShowEmailCapture(true);
        }
      } catch (e) {
        toast.error("Laporan tidak dijumpai.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const pay = async () => {
    if (!checkout.email || !checkout.phone) {
      toast.error("Sila masukkan emel dan nombor telefon.");
      return;
    }
    setPaying(true);
    try {
      const res = await startPayment(id, checkout);
      if (res.already_paid) { window.location.reload(); return; }
      if (res.payment_url) { window.location.assign(res.payment_url); }
      else throw new Error("no url");
    } catch (e) {
      setPaying(false);
      const msg = e?.response?.status === 503
        ? "Pembayaran belum dikonfigurasi. Sila hubungi pentadbir."
        : "Gagal memulakan pembayaran. Sila cuba lagi.";
      toast.error(msg);
    }
  };

  const doDownload = async () => {
    setDownloading(true);
    try {
      const blob = await downloadAssessmentPdf(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `laporan-growth-factor-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Laporan PDF dimuat turun.");
    } catch (e) {
      toast.error("Gagal memuat turun laporan. Sila cuba lagi.");
    } finally {
      setDownloading(false);
    }
  };

  const doDownloadSnapshot = async () => {
    setSnapshotting(true);
    try {
      const blob = await downloadAssessmentSnapshot(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `growth-snapshot-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("A4 Growth Snapshot dimuat turun.");
    } catch (e) {
      toast.error("Gagal memuat turun snapshot. Sila cuba lagi.");
    } finally {
      setSnapshotting(false);
    }
  };

  const doShare = async () => {
    const shareUrl = `${window.location.origin}/report/${id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Laporan Growth Factor", url: shareUrl });
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Pautan laporan disalin.");
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      // user cancelled share sheet or clipboard blocked
    }
  };

  if (loading) {
    return (
      <GFFrame>
        <div className="flex min-h-screen flex-col items-center justify-center gap-3 text-slate-400">
          <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
          <span className="text-sm">Mengesahkan status pembayaran…</span>
        </div>
      </GFFrame>
    );
  }
  if (!d) {
    return (
      <GFFrame>
        <div className="flex min-h-screen items-center justify-center text-slate-400">Laporan tidak dijumpai.</div>
      </GFFrame>
    );
  }

  // ---------------- UNPAID: checkout (unchanged) ----------------
  if (!d.paid) {
    return (
      <GFFrame testId="checkout-page">
        <div className="flex min-h-screen flex-col">
          <GFHeader />
          <button onClick={() => navigate(`/result/${id}`)} data-testid="back-to-results-btn"
            className="ml-6 mt-2 flex w-fit items-center gap-1 text-sm text-slate-400">
            <ArrowLeft className="h-4 w-4" /> Kembali ke keputusan
          </button>
          <div className="flex flex-1 flex-col px-6 pb-10 pt-4">
            <div className="gf-gold-line mb-5" />
            <h2 className="font-heading text-3xl font-bold text-[#0F172A]">Buka Laporan Penuh</h2>
            <p className="mt-2 text-sm text-slate-500">
              Laporan penuh Growth Factor untuk{" "}
              <span className="font-semibold text-[#0F172A]">{d.user.nama_bisnes}</span>.
            </p>
            <div className="mt-6 rounded-2xl bg-[#0F172A] p-6 text-white">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Laporan Penuh Growth Factor</span>
              <div className="mt-2 flex items-end gap-2">
                <span className="font-heading text-4xl font-bold text-[#D4AF37]">RM15</span>
                <span className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">Harga Pengenalan</span>
              </div>
            </div>
            <div className="mt-6 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-600">Emel</label>
                <input data-testid="checkout-email" type="email" className={inputCls} placeholder="emel@contoh.com"
                  value={checkout.email} onChange={(e) => setCheckout({ ...checkout, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-600">No. Telefon</label>
                <input data-testid="checkout-phone" className={inputCls} placeholder="0123456789"
                  value={checkout.phone} onChange={(e) => setCheckout({ ...checkout, phone: e.target.value })} />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              Pembayaran selamat melalui ToyyibPay (FPX / kad kredit).
            </div>
            <div className="mt-auto pt-8">
              <button data-testid="pay-toyyibpay-btn" onClick={pay} disabled={paying}
                className="flex w-full items-center justify-center gap-3 rounded-full bg-[#D4AF37] py-4 text-base font-bold text-[#0F172A] shadow-[0_10px_30px_rgba(212,175,55,0.35)] transition-transform duration-200 active:scale-[0.98] disabled:opacity-60">
                {paying ? (<><Loader2 className="h-5 w-5 animate-spin" /> Menyediakan pembayaran…</>) : "BAYAR RM15 SEKARANG"}
              </button>
            </div>
          </div>
        </div>
      </GFFrame>
    );
  }

  // ---------------- PAID: deep diagnostic report (Phase 1) ----------------
  const r = d.full_report;
  const arc = r.archetype;
  const ls = r.brand_marketing_diagnosis.live_stream;

  return (
    <GFFrame testId="report-page">
      <div className="min-h-screen pb-16">
        {/* Header */}
        <div className="bg-[#0F172A] px-6 pb-8 pt-10 text-white">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-400">Pembayaran Disahkan</span>
          </div>
          <h1 className="mt-3 font-heading text-3xl font-bold text-[#D4AF37]">Laporan Growth Factor</h1>
          <div className="mt-5 grid grid-cols-2 gap-y-3 text-sm">
            <div><p className="text-slate-500">Nama</p><p className="font-semibold">{d.user.nama}</p></div>
            <div><p className="text-slate-500">Nama Bisnes</p><p className="font-semibold">{d.user.nama_bisnes}</p></div>
            <div><p className="text-slate-500">Industri</p><p className="font-semibold">{d.user.industri}</p></div>
            <div><p className="text-slate-500">Growth Factor</p><p className="font-semibold text-[#D4AF37]">{d.overall} · {d.category.label}</p></div>
          </div>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2">
            <Feather className="h-4 w-4 text-[#D4AF37]" />
            <span className="text-xs text-slate-300">Business Archetype:</span>
            <span className="text-sm font-bold text-[#D4AF37]">{arc.name}</span>
          </div>

          {/* Download + Share (paid only) */}
          <div className="mt-5 flex gap-3" data-testid="report-actions">
            <button
              data-testid="download-report-btn"
              onClick={doDownload}
              disabled={downloading}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#D4AF37] py-3 text-sm font-bold text-[#0F172A] transition-transform active:scale-[0.98] disabled:opacity-60"
            >
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Muat Turun Laporan
            </button>
            <button
              data-testid="share-report-btn"
              onClick={doShare}
              className="flex flex-1 items-center justify-center gap-2 rounded-full border border-white/25 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10"
            >
              {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Share2 className="h-4 w-4" />}
              {copied ? "Pautan Disalin" : "Kongsi / Salin Pautan"}
            </button>
          </div>
          <button
            data-testid="download-snapshot-btn"
            onClick={doDownloadSnapshot}
            disabled={snapshotting}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border-2 border-[#D4AF37] py-3 text-sm font-bold text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10 disabled:opacity-60"
          >
            {snapshotting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Download A4 Growth Snapshot
          </button>
        </div>

        {/* Brand2Market AI — turn diagnosis into action */}
        <AiCtaSection
          credits={aiCredits}
          assessmentId={id}
          email={ownerEmail}
          onCreate={() => navigate(`/campaigns/new?assessment_id=${id}${ownerEmail ? `&email=${encodeURIComponent(ownerEmail)}` : ""}`)}
          onViewAll={() => navigate(`/campaigns${ownerEmail ? `?email=${encodeURIComponent(ownerEmail)}` : ""}`)}
        />

        <EmailCaptureModal
          open={showEmailCapture}
          assessmentId={id}
          onSaved={(email) => { setShowEmailCapture(false); loadOwner(email); }}
        />

        {/* 1. Executive Diagnosis */}
        <Section kicker="Bahagian 1" title="Diagnosis Eksekutif" testId="report-executive-diagnosis">
          <div className="space-y-3">
            {r.executive_diagnosis.paragraphs.map((p, i) => (
              <p key={i} className="text-sm leading-relaxed text-slate-600">{p}</p>
            ))}
          </div>
          <div className="mt-4 rounded-2xl border-l-4 border-[#D4AF37] bg-[#FFFBEF] p-4">
            <p className="text-xs font-bold uppercase tracking-widest text-[#B8912B]">Diagnosis Utama</p>
            <p className="mt-1.5 text-sm font-medium leading-relaxed text-[#0F172A]">{r.executive_diagnosis.diagnosis_utama}</p>
          </div>
        </Section>

        {/* Phase 2 — Adaptive personalisation layer */}
        {r.personalisation && (
          <Section kicker="Disesuaikan Untuk Profil Ini" title="Penyesuaian Mengikut Profil" icon={Compass} testId="report-personalisation">
            <div className="rounded-2xl bg-[#0F172A] p-5 text-white">
              <p className="text-sm leading-relaxed text-slate-200">{r.personalisation.context_summary}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-slate-300">
                  Pengalaman: <span className="font-bold text-[#D4AF37]">{r.personalisation.experience_level.label}</span>
                </span>
                <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-slate-300">
                  Kapasiti: <span className="font-bold text-[#D4AF37]">{r.personalisation.capacity_level.label}</span>
                </span>
                <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-slate-300">
                  Nada: <span className="font-bold text-[#D4AF37]">{r.personalisation.communication_style}</span>
                </span>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {r.personalisation.focus_guidance.map((g, i) => (
                <div key={i} className="rounded-2xl border border-[#E7E2D6] bg-white p-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-[#B8912B]">{g.label}</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">{g.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-2xl border-l-4 border-[#D4AF37] bg-[#FFFBEF] p-4">
              <p className="text-xs font-bold uppercase tracking-widest text-[#B8912B]">Cara Menggunakan Laporan Ini</p>
              <p className="mt-1.5 text-sm leading-relaxed text-[#0F172A]">{r.personalisation.how_to_approach}</p>
            </div>
          </Section>
        )}

        {/* 2. Dashboard */}
        <Section kicker="Bahagian 2" title="Growth Factor Dashboard" testId="report-dashboard">
          <div className="space-y-4">
            {r.dashboard.map((f) => (
              <div key={f.key} className="rounded-2xl border border-[#E7E2D6] bg-white p-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-bold text-[#0F172A]">{f.label}</span>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${TONE_STYLE[f.tone]}`}>{f.status}</span>
                </div>
                <FactorBar label="" score={f.score} highlight={f.key === d.driver.key} />
              </div>
            ))}
          </div>
        </Section>

        {/* 3. Deep diagnostic (interpretation only, collapsible) */}
        <Section kicker="Bahagian 3" title="Analisis Diagnostik Mendalam" testId="report-deepdive">
          <Accordion type="single" collapsible defaultValue={r.deep_diagnostic[0]?.key} className="space-y-3">
            {r.deep_diagnostic.map((f) => (
              <AccordionItem key={f.key} value={f.key} className="overflow-hidden rounded-2xl border border-[#E7E2D6] bg-white !border-b">
                <AccordionTrigger className="px-4 hover:no-underline" data-testid={`deepdive-trigger-${f.key}`}>
                  <span className="flex items-center gap-3">
                    <span className="font-heading text-base font-bold text-[#0F172A]">{f.label}</span>
                    <span className="text-sm font-bold text-[#D4AF37]">{f.score}%</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-4">
                  <div className="space-y-3 pt-1">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Apa yang skor ini beritahu</p>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{f.apa_skor}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Apa yang mungkin sedang berlaku</p>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{f.apa_berlaku}</p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Section>

        {/* 4. Financial & profitability */}
        <Section kicker="Bahagian 4" title="Diagnosis Kewangan & Keuntungan" icon={Wallet} testId="report-financial">
          <Card>
            <p className="text-sm leading-relaxed text-slate-600">{r.financial_diagnosis.intro}</p>

            <p className="mt-4 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#0F172A]">
              <Calculator className="h-4 w-4 text-[#D4AF37]" /> Management Account Bulanan
            </p>
            <div className="mt-2 divide-y divide-slate-100 rounded-xl bg-[#FBFAF6] px-3">
              {r.financial_diagnosis.management_account.map((m, i) => (
                <div key={i} className="py-2">
                  <p className="text-sm font-semibold text-[#0F172A]">{m.item}</p>
                  <p className="text-xs text-slate-500">{m.note}</p>
                </div>
              ))}
            </div>

            <p className="mt-4 text-xs font-bold uppercase tracking-wide text-[#0F172A]">Formula Penting</p>
            <div className="mt-2 space-y-2">
              {r.financial_diagnosis.formulas.map((f, i) => (
                <div key={i} className="rounded-xl bg-[#0F172A] px-4 py-2.5 text-white">
                  <p className="text-xs text-[#D4AF37]">{f.name}</p>
                  <p className="text-sm font-semibold">{f.formula}</p>
                </div>
              ))}
            </div>

            <p className="mt-4 text-xs font-bold uppercase tracking-wide text-[#0F172A]">Soalan Diagnostik</p>
            <ul className="mt-2 space-y-2">
              {r.financial_diagnosis.diagnostic_questions.map((q, i) => (
                <li key={i} className="rounded-xl border border-[#E7E2D6] px-3 py-2 text-sm italic text-slate-600">“{q}”</li>
              ))}
            </ul>

            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/50 p-3">
              <p className="text-xs font-bold uppercase tracking-wide text-amber-700">Nota COGS</p>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">{r.financial_diagnosis.cogs_note}</p>
            </div>

            <p className="mt-4 text-xs font-bold uppercase tracking-wide text-[#B8912B]">Tindakan Pembetulan</p>
            <ActionList items={r.financial_diagnosis.corrective_actions} />
          </Card>
        </Section>

        {/* 4b. Financial projection (blueprint — mirrors A4 & PDF) */}
        {r.projection?.has_data && (
          <Section kicker="Blueprint" title="Unjuran Kewangan 6 Bulan" icon={TrendingUp} testId="report-projection">
            {r.projection.figures?.length > 0 && (
              <div className="grid grid-cols-2 gap-3">
                {r.projection.figures.map((f, i) => (
                  <div key={i} className="rounded-2xl border border-[#E7E2D6] bg-[#FBFAF6] p-4" data-testid={`proj-fig-${i}`}>
                    <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{f.label}</p>
                    <p className="mt-1 font-heading text-lg font-bold text-[#0F172A]">{f.value}</p>
                  </div>
                ))}
              </div>
            )}
            {r.projection.customers_needed && (
              <div className="mt-4 flex gap-3 rounded-2xl border-l-4 border-emerald-500 bg-emerald-50/60 p-4">
                <Calculator className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                <p className="text-sm leading-relaxed text-slate-700">
                  Untuk menutup jurang <b>{r.projection.gap}</b> sebulan pada purata transaksi <b>{r.projection.avg_txn}</b>:
                  perlukan lebih kurang <b>{r.projection.customers_needed} pelanggan tambahan sebulan</b> (~{r.projection.per_week} seminggu).
                </p>
              </div>
            )}
            {r.projection.series?.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-xs font-bold uppercase tracking-wide text-[#B8912B]">Sasaran Berperingkat</p>
                {r.projection.series.map((m, i) => {
                  const max = r.projection.series[r.projection.series.length - 1].value || 1;
                  return (
                    <div key={i} className="flex items-center gap-3" data-testid={`proj-month-${i}`}>
                      <span className="w-16 text-xs font-semibold text-slate-500">{m.label}</span>
                      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.max(6, (m.value / max) * 100)}%` }} />
                      </div>
                      <span className="w-24 text-right text-xs font-bold text-[#0F172A]">{m.value_rm}</span>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="mt-3 text-xs italic leading-relaxed text-slate-400">{r.projection.disclaimer}</p>
          </Section>
        )}

        {/* 4c. 3-6 month roadmap (blueprint — mirrors A4 & PDF) */}
        {r.roadmap_6month?.length > 0 && (
          <Section kicker="Blueprint" title="Pelan 3–6 Bulan" icon={CalendarDays} testId="report-roadmap">
            <div className="space-y-3">
              {r.roadmap_6month.map((m, i) => (
                <div key={i} className="rounded-2xl border border-[#E7E2D6] bg-white p-4" data-testid={`roadmap-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0F172A] text-[11px] font-bold text-[#D4AF37]">{i + 1}</span>
                    <p className="font-heading text-sm font-bold text-[#0F172A]">{m.title}</p>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{m.focus}</p>
                  <p className="mt-1.5 text-xs font-semibold text-[#B8912B]">KPI: {m.kpi}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* 5. Customer & market */}
        <Section kicker="Bahagian 5" title="Diagnosis Pelanggan & Pasaran" icon={Users} testId="report-customer">
          <Card>
            <p className="text-sm leading-relaxed text-slate-600">{r.customer_market_diagnosis.observation}</p>
            <p className="mt-4 text-xs font-bold uppercase tracking-wide text-[#0F172A]">Soalan Diagnostik</p>
            <ul className="mt-2 space-y-2">
              {r.customer_market_diagnosis.diagnostic_questions.map((q, i) => (
                <li key={i} className="rounded-xl border border-[#E7E2D6] px-3 py-2 text-sm italic text-slate-600">“{q}”</li>
              ))}
            </ul>
            <p className="mt-4 text-xs font-bold uppercase tracking-wide text-[#B8912B]">Tindakan</p>
            <ActionList items={r.customer_market_diagnosis.actions} />
          </Card>
        </Section>

        {/* 6. Brand & marketing (industry adaptive) */}
        <Section kicker="Bahagian 6" title="Diagnosis Jenama & Pemasaran" icon={Megaphone} testId="report-brand">
          <Card>
            <p className="text-sm leading-relaxed text-slate-600">{r.brand_marketing_diagnosis.observation}</p>

            <p className="mt-4 text-xs font-bold uppercase tracking-wide text-[#0F172A]">Saluran Disyorkan</p>
            <div className="mt-2 space-y-2">
              {r.brand_marketing_diagnosis.channels.map((c, i) => (
                <div key={i} className="rounded-xl bg-[#FBFAF6] p-3">
                  <p className="text-sm font-semibold text-[#0F172A]">{c.name}</p>
                  <p className="text-xs text-slate-500">{c.why}</p>
                </div>
              ))}
            </div>

            <p className="mt-4 text-xs font-bold uppercase tracking-wide text-[#0F172A]">Contoh Kandungan Minggu Ini</p>
            <ol className="mt-2 space-y-2">
              {r.brand_marketing_diagnosis.content_examples.map((c, i) => (
                <li key={i} className="flex gap-3 text-sm text-slate-600">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#D4AF37] text-[10px] font-bold text-[#0F172A]">{i + 1}</span>
                  {c}
                </li>
              ))}
            </ol>

            {ls && (
              <div className="mt-4 rounded-xl border border-[#0F172A]/10 bg-[#0F172A] p-4 text-white">
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#D4AF37]">
                  <Radio className="h-4 w-4" /> Panduan Live Streaming
                </p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-300">
                  <p><span className="text-slate-500">Platform:</span> {ls.platform}</p>
                  <p><span className="text-slate-500">Tempoh:</span> {ls.duration}</p>
                  <p><span className="text-slate-500">Kekerapan:</span> {ls.frequency}</p>
                  <p><span className="text-slate-500">Objektif:</span> {ls.objective}</p>
                </div>
                <p className="mt-3 text-xs font-bold uppercase tracking-wide text-slate-400">Struktur</p>
                <ul className="mt-1 space-y-1">
                  {ls.structure.map((s, i) => (<li key={i} className="text-sm text-slate-300">{s}</li>))}
                </ul>
                <div className="mt-3 rounded-lg bg-white/5 p-3">
                  <p className="text-xs text-slate-500">Contoh Pembukaan:</p>
                  <p className="text-sm italic text-slate-200">“{ls.opening}”</p>
                  <p className="mt-2 text-xs text-slate-500">Contoh CTA:</p>
                  <p className="text-sm italic text-[#D4AF37]">“{ls.cta}”</p>
                </div>
              </div>
            )}

            <p className="mt-4 text-xs font-bold uppercase tracking-wide text-[#B8912B]">Tindakan</p>
            <ActionList items={r.brand_marketing_diagnosis.actions} />
          </Card>
        </Section>

        {/* 6b. Social Media Marketing Playbook */}
        {r.social_playbook && (
          <Section kicker="Panduan Tambahan" title="Panduan Pemasaran Media Sosial 90 Hari" icon={Megaphone} testId="report-social-playbook">
            <p className="text-sm leading-relaxed text-[#D6E7DF]">{r.social_playbook.intro}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {r.social_playbook.platforms.map((p, i) => (
                <span key={i} className="rounded-full bg-white/10 px-3 py-1.5 text-xs text-slate-200" data-testid={`sp-platform-${i}`}>
                  <b className="text-[#D4AF37]">{p.name}</b> — {p.why}
                </span>
              ))}
            </div>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {r.social_playbook.months.map((m, i) => (
                <div key={i} className="rounded-2xl bg-white/5 p-5" data-testid={`sp-month-${i}`}>
                  <h4 className="font-heading text-base font-bold text-[#D4AF37]">{m.title}</h4>
                  <p className="mt-1 text-xs italic text-slate-300">{m.focus}</p>
                  <p className="mt-3 text-[11px] font-bold uppercase tracking-wide text-emerald-300">Buat</p>
                  <ul className="mt-1 space-y-1.5">
                    {m.do.map((a, j) => (<li key={j} className="text-xs leading-relaxed text-slate-200">✓ {a}</li>))}
                  </ul>
                  <p className="mt-3 text-[11px] font-bold uppercase tracking-wide text-amber-300">Kemas Kini</p>
                  <ul className="mt-1 space-y-1.5">
                    {m.update.map((u, j) => (<li key={j} className="text-xs leading-relaxed text-slate-300">↻ {u}</li>))}
                  </ul>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* 6c. Blind Spots (archetype-personalised) */}
        {r.blind_spots && (
          <Section kicker="Refleksi Jujur" title="Titik Buta & Maksudnya" icon={AlertTriangle} testId="report-blind-spots">
            <p className="text-sm leading-relaxed text-[#D6E7DF]">{r.blind_spots}</p>
          </Section>
        )}

        {/* 6d. 30-Day Action Plan (archetype-toned) */}
        {r.action_plan_30d && (
          <Section kicker="Langkah Konkrit" title="Pelan Tindakan 30 Hari" icon={TrendingUp} testId="report-action-plan-30d">
            <div className="space-y-3">
              {r.action_plan_30d.map((a, i) => (
                <div key={i} className="rounded-2xl bg-white/5 p-4" data-testid={`ap30-${i}`}>
                  <p className="text-xs font-bold uppercase tracking-wide text-[#D4AF37]">{a.week} · {a.factor} ({a.score}%)</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-200">{a.text}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* 7. Operations */}
        <Section kicker="Bahagian 7" title="Diagnosis Operasi & Sistem" icon={Settings2} testId="report-operations">
          <Card>
            <p className="text-sm leading-relaxed text-slate-600">{r.operations_diagnosis.observation}</p>
            <p className="mt-4 text-xs font-bold uppercase tracking-wide text-[#B8912B]">Tindakan</p>
            <ActionList items={r.operations_diagnosis.actions} />
          </Card>
        </Section>

        {/* 8. Owner capacity */}
        <Section kicker="Bahagian 8" title="Kapasiti Pemilik Bisnes" icon={UserCog} testId="report-owner">
          <Card>
            <p className="text-sm leading-relaxed text-slate-600">{r.owner_capacity.observation}</p>
            <p className="mt-4 text-xs font-bold uppercase tracking-wide text-[#B8912B]">Tindakan</p>
            <ActionList items={r.owner_capacity.actions} />
          </Card>
        </Section>

        {/* 9. Archetype */}
        <Section kicker="Bahagian 9" title="Business Archetype" testId="report-archetype">
          <div className="overflow-hidden rounded-2xl bg-[#0F172A] text-white">
            <div className="h-40 w-full bg-cover bg-center" style={{ backgroundImage: `url(${ARCHETYPE_IMAGES[arc.image_key]})` }} />
            <div className="p-5">
              <h4 className="font-heading text-2xl font-bold text-[#D4AF37]">{arc.name}</h4>
              <p className="text-sm text-slate-400">{arc.tagline}</p>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">{arc.meaning}</p>
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-widest text-emerald-400">Kekuatan Tipikal</p>
                <ul className="mt-2 space-y-1.5">
                  {arc.strengths.map((s, i) => (<li key={i} className="flex gap-2 text-sm text-slate-300"><span className="text-emerald-400">·</span> {s}</li>))}
                </ul>
              </div>
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-widest text-amber-400">Titik Buta Tipikal</p>
                <ul className="mt-2 space-y-1.5">
                  {arc.blind_spots.map((s, i) => (<li key={i} className="flex gap-2 text-sm text-slate-300"><span className="text-amber-400">·</span> {s}</li>))}
                </ul>
              </div>
              <div className="mt-4 space-y-3">
                <div className="rounded-xl bg-white/5 p-3"><p className="text-xs font-bold uppercase tracking-widest text-slate-400">Ketika Bawah Tekanan</p><p className="mt-1.5 text-sm text-slate-300">{arc.under_pressure}</p></div>
                <div className="rounded-xl bg-white/5 p-3"><p className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">Peluang Pertumbuhan</p><p className="mt-1.5 text-sm text-slate-300">{arc.growth_opportunity}</p></div>
                <div className="rounded-xl bg-white/5 p-3"><p className="text-xs font-bold uppercase tracking-widest text-emerald-400">Fokus Seterusnya</p><p className="mt-1.5 text-sm text-slate-300">{arc.focus_next}</p></div>
                <div className="rounded-xl bg-white/5 p-3"><p className="text-xs font-bold uppercase tracking-widest text-slate-400">Keserasian</p><p className="mt-1.5 text-sm text-slate-300">{arc.compatibility}</p></div>
              </div>
            </div>
          </div>
        </Section>

        {/* 10. Priority matrix */}
        <Section kicker="Bahagian 10" title="Priority Matrix" testId="report-priority">
          <div className="space-y-4">
            <PriorityBlock icon={Zap} tone="rose" heading="Buat Sekarang" subtitle="Berimpak tinggi & mendesak" items={r.priority_matrix.buat_sekarang} />
            <PriorityBlock icon={Clock} tone="amber" heading="Buat Seterusnya" subtitle="Penting tetapi kurang mendesak" items={r.priority_matrix.buat_seterusnya} />
            <PriorityBlock icon={PauseCircle} tone="slate" heading="Tangguhkan Dahulu" subtitle="Elak mengalih fokus" items={r.priority_matrix.tangguhkan} />
          </div>
        </Section>

        {/* 11. 30-day prescription */}
        <Section kicker="Bahagian 11" title="Preskripsi 30 Hari" icon={CalendarDays} testId="report-action-plan">
          <div className="space-y-4">
            {r.prescription_30_day.map((wk, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
                className="rounded-2xl border border-[#E7E2D6] bg-white p-5">
                <span className="font-heading text-base font-bold text-[#0F172A]">{wk.title}</span>
                <p className="mt-1 text-sm font-medium text-[#B8912B]">{wk.objective}</p>
                <ul className="mt-3 space-y-2">
                  {wk.tasks.map((a, j) => (
                    <li key={j} className="flex gap-3 text-sm text-slate-600">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#0F172A] text-[10px] font-bold text-white">{j + 1}</span>{a}
                    </li>
                  ))}
                </ul>
                <div className="mt-3 grid grid-cols-1 gap-1.5 rounded-xl bg-[#FBFAF6] p-3 text-xs text-slate-600">
                  <p><span className="font-semibold text-[#0F172A]">Output: </span>{wk.output}</p>
                  <p><span className="font-semibold text-[#0F172A]">Metrik: </span>{wk.metric}</p>
                  <p><span className="font-semibold text-[#0F172A]">Masa: </span>{wk.time}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Section>

        {/* 12. Metrics */}
        <Section kicker="Bahagian 12" title="Metrik Untuk Dipantau" icon={Gauge} testId="report-metrics">
          <div className="space-y-3">
            {r.metrics.map((m, i) => (
              <Card key={i} className="p-4">
                <div className="flex items-center gap-2">
                  <Gauge className="h-4 w-4 text-[#D4AF37]" />
                  <span className="text-sm font-bold text-[#0F172A]">{m.name}</span>
                </div>
                <div className="mt-2 space-y-1 text-sm text-slate-600">
                  <p><span className="font-semibold text-[#0F172A]">Apa: </span>{m.what}</p>
                  <p><span className="font-semibold text-[#0F172A]">Kenapa: </span>{m.why}</p>
                  <p><span className="font-semibold text-[#0F172A]">Kekerapan: </span>{m.frequency}</p>
                </div>
              </Card>
            ))}
          </div>
        </Section>

        {/* 13. Catatan Dr. Irma */}
        <Section kicker="Bahagian 13" title="Catatan Dr. Irma" testId="report-consultant-note">
          <div className="rounded-2xl bg-[#0F172A] p-6 text-white">
            <div className="flex items-center gap-2 text-[#D4AF37]">
              <Target className="h-4 w-4" />
              <span className="text-sm font-bold italic">{r.consultant_note.philosophy}</span>
            </div>
            <div className="mt-4 space-y-3">
              {r.consultant_note.paragraphs.map((p, i) => (<p key={i} className="text-sm leading-relaxed text-slate-300">{p}</p>))}
            </div>
            <div className="mt-5 rounded-xl border-l-2 border-[#D4AF37] bg-white/5 p-4">
              <Quote className="h-4 w-4 text-[#D4AF37]" />
              <p className="mt-2 font-heading text-base italic leading-relaxed text-white">{r.consultant_note.quote}</p>
            </div>
            <div className="mt-5">
              {r.consultant_note.signature.map((line, i) => (
                <p key={i} className={i === 0 ? "font-heading text-base font-bold text-[#D4AF37]" : "text-xs text-slate-400"}>{line}</p>
              ))}
            </div>
          </div>
        </Section>

        <div className="mt-10 px-6 text-center">
          <p className="text-xs text-slate-400">Laporan ini dijana berdasarkan respons yang diberikan dan bertujuan sebagai panduan diagnostik reflektif.</p>
          <button onClick={() => navigate("/")} data-testid="report-home-btn"
            className="mt-4 rounded-full border border-[#E7E2D6] px-6 py-2.5 text-sm font-semibold text-[#0F172A]">
            Kembali ke Laman Utama
          </button>
        </div>
      </div>
    </GFFrame>
  );
}

const TONE = { rose: "text-rose-600", amber: "text-amber-600", slate: "text-slate-500" };

const PriorityBlock = ({ icon: Icon, tone, heading, subtitle, items }) => (
  <div className="rounded-2xl border border-[#E7E2D6] bg-white p-5">
    <div className="flex items-center gap-2">
      <Icon className={`h-5 w-5 ${TONE[tone]}`} />
      <span className="font-heading text-base font-bold text-[#0F172A]">{heading}</span>
    </div>
    <p className="mt-0.5 text-xs text-slate-400">{subtitle}</p>
    <div className="mt-3 space-y-3">
      {items.map((it, i) => (
        <div key={i} className="border-l-2 border-slate-100 pl-3">
          <p className="text-sm font-medium text-[#0F172A]">{it.action}</p>
          <p className="mt-0.5 text-xs text-slate-500">{it.reason}</p>
        </div>
      ))}
    </div>
  </div>
);
